/**
 * Work List — Excel 내보내기. PDF용 `DocumentWorkListHorizontal` 헤더·열 순서와 맞춤.
 */

import ExcelJS from 'exceljs';
import type { WorkListPdfTableRow } from './DocumentWorkListHorizontal';
import { rasterBlobToJpegBlob, sniffWebpMagicPrefix } from '../../shared/util/workListExportThumbRaster';

export type BuildWorkListXlsxOpts = {
  includeArtist: boolean;
  includePrice: boolean;
  includeNote: boolean;
  singleLineCaption?: boolean;
  captionHeaderLabel?: string;
};

const IMAGE_COL_ZERO_BASED = 1;
/** 행 높이 pts 계산 기준(px). 실제 삽입 크기는 열 너비·행 높이에서 유도 */
const THUMB_EMBED_PX = 80;
/** fetch 동시 진행 개수 — 너무 많으면 브라우저/서버 부담 */
const THUMB_FETCH_CONCURRENCY = 6;

/** ExcelJS `ext-xform`과 동일 — 앵커 colOff/rowOff(EMUs) 계산용 */
const EMU_PER_PIXEL_96 = 9525;

/** Excel 열 너비(문자 단위) · `WorkListExcelPreviewVertical` `colgroup` 비율과 동일 가중치 */
const WORK_LIST_XLSX_COL_WIDTH = {
  no: 6,
  image: 14,
  title: 28,
  artist: 17,
  year: 8,
  material: 18,
  size: 16,
  caption: 28,
  price: 14,
  note: 20,
} as const;

/** 열 순서: No. → Image → (Artist) → Title → Year → Material → Size → 선택 열. */
export function getWorkListXlsxColumnWidthWeights(
  includeArtist: boolean,
  includePrice: boolean,
  includeNote: boolean,
  singleLineCaption = false,
): number[] {
  const w: number[] = [
    WORK_LIST_XLSX_COL_WIDTH.no,
    WORK_LIST_XLSX_COL_WIDTH.image,
    ...(includeArtist ? [WORK_LIST_XLSX_COL_WIDTH.artist] : []),
    WORK_LIST_XLSX_COL_WIDTH.title,
    WORK_LIST_XLSX_COL_WIDTH.year,
    WORK_LIST_XLSX_COL_WIDTH.material,
    WORK_LIST_XLSX_COL_WIDTH.size,
  ];
  if (includePrice) w.push(WORK_LIST_XLSX_COL_WIDTH.price);
  if (includeNote) w.push(WORK_LIST_XLSX_COL_WIDTH.note);
  if (singleLineCaption) w.push(WORK_LIST_XLSX_COL_WIDTH.caption);
  return w;
}

/** Excel 내보내기·미리보기 공통 푸터 문구 */
export const WORK_LIST_XLSX_EXPORT_FOOTNOTE_EN =
  'This data was exported from Architrave.';

/** Excel 시트 컬럼 인덱스(1=A) → A, B, …, Z, AA */
const excelColumnLetter1Based = (col1Based: number): string => {
  let n = col1Based;
  let s = '';
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
};

/** ExcelJS가 허용하는 확장자만 */
type ExcelImageExtension = 'jpeg' | 'png' | 'gif';

const sniffEmbeddedExtension = (bytes: Uint8Array): ExcelImageExtension | null => {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff)
    return 'jpeg';
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47
  )
    return 'png';
  if (bytes.length >= 6 && bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) return 'gif';
  return null;
};

const mapInBatches = async <T, R>(
  items: readonly T[],
  batchSize: number,
  mapper: (item: T, index: number) => Promise<R>,
): Promise<R[]> => {
  const out: R[] = [];
  for (let i = 0; i < items.length; i += batchSize) {
    const slice = items.slice(i, i + batchSize);
    const chunk = await Promise.all(slice.map((item, j) => mapper(item, i + j)));
    out.push(...chunk);
  }
  return out;
};

/** Excel 열 너비(문자 단위)를 대략적인 픽셀 폭으로 환산 — 삽입 이미지 가로 상한(contain 박스) */
function excelColumnCharsToInnerWidthPx(colWidthChars: number): number {
  return Math.max(40, Math.round(colWidthChars * 7 + 10));
}

/** 행 높이(pt)를 대략적인 픽셀 높이로 환산 — 삽입 이미지 세로 상한(테두리·여백 여유) */
function excelRowHeightPtsToInnerHeightPx(heightPts: number): number {
  return Math.max(32, Math.round(heightPts * (96 / 72) - 8));
}

/** 가로 max × 세로 max 직사각형 안에 원본 비율 유지(contain) */
function thumbExtentsContain(
  naturalW: number,
  naturalH: number,
  maxWidthPx: number,
  maxHeightPx: number,
): { width: number; height: number } {
  if (naturalW <= 0 || naturalH <= 0) {
    const side = Math.min(maxWidthPx, maxHeightPx);
    return { width: side, height: side };
  }
  const scale = Math.min(maxWidthPx / naturalW, maxHeightPx / naturalH);
  return {
    width: Math.max(1, Math.round(naturalW * scale)),
    height: Math.max(1, Math.round(naturalH * scale)),
  };
}

async function measureNaturalSizeFromImageBuffer(buffer: ArrayBuffer): Promise<{ w: number; h: number } | null> {
  if (typeof Image === 'undefined') return null;
  const blob = new Blob([buffer]);
  const objUrl = URL.createObjectURL(blob);
  try {
    return await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        resolve(w > 0 && h > 0 ? { w, h } : null);
      };
      img.onerror = () => resolve(null);
      img.src = objUrl;
    });
  } finally {
    URL.revokeObjectURL(objUrl);
  }
}

async function fetchThumbEmbedded(
  url: string,
  rasterDecodeMaxPx: number,
): Promise<{ buffer: ArrayBuffer; extension: ExcelImageExtension } | null> {
  const trimmed = url.trim();
  if (!trimmed) return null;
  try {
    const res = await fetch(trimmed, { mode: 'cors' });
    if (!res.ok) return null;
    const blob = await res.blob();
    const ctype = res.headers.get('content-type') || '';
    if (ctype.includes('jpeg') || ctype.includes('jpg')) {
      const ab = await blob.arrayBuffer();
      return { buffer: ab, extension: 'jpeg' };
    }
    if (ctype.includes('png')) {
      const ab = await blob.arrayBuffer();
      return { buffer: ab, extension: 'png' };
    }
    if (ctype.includes('gif')) {
      const ab = await blob.arrayBuffer();
      return { buffer: ab, extension: 'gif' };
    }
    if (ctype.toLowerCase().includes('webp')) {
      const jpegBlob = await rasterBlobToJpegBlob(blob, rasterDecodeMaxPx);
      if (!jpegBlob) return null;
      const ab = await jpegBlob.arrayBuffer();
      return { buffer: ab, extension: 'jpeg' };
    }

    const abAll = await blob.arrayBuffer();
    const u8 = new Uint8Array(abAll);
    const sniffed = sniffEmbeddedExtension(u8);
    if (sniffed) return { buffer: abAll, extension: sniffed };

    if (!sniffWebpMagicPrefix(u8)) return null;

    const jpegBlob = await rasterBlobToJpegBlob(blob, rasterDecodeMaxPx);
    if (!jpegBlob) return null;
    const jpegBuf = await jpegBlob.arrayBuffer();
    return { buffer: jpegBuf, extension: 'jpeg' };
  } catch {
    return null;
  }
}

export async function buildWorkListXlsxBlob(
  tableRows: WorkListPdfTableRow[],
  opts: BuildWorkListXlsxOpts,
): Promise<Blob> {
  const {
    includeArtist,
    includePrice,
    includeNote,
    singleLineCaption = false,
    captionHeaderLabel = 'Caption',
  } = opts;
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Architrave';
  const sheet = workbook.addWorksheet('Work List');

  const headers = [
    'No.',
    'Image',
    ...(includeArtist ? ['Artist'] : []),
    'Title',
    'Year',
    'Material',
    'Size',
    ...(includePrice ? ['Price'] : []),
    ...(includeNote ? ['Note'] : []),
    ...(singleLineCaption ? [captionHeaderLabel] : []),
  ];

  const columnWeights = getWorkListXlsxColumnWidthWeights(
    includeArtist,
    includePrice,
    includeNote,
    singleLineCaption,
  );
  const imageColChars = columnWeights[1] ?? WORK_LIST_XLSX_COL_WIDTH.image;
  const imageCellMaxWidthPx = excelColumnCharsToInnerWidthPx(imageColChars);

  const rowHeightPts = Math.max((THUMB_EMBED_PX / 96) * 72 + 4, 62);
  const imageCellMaxHeightPx = excelRowHeightPtsToInnerHeightPx(rowHeightPts);

  const rasterDecodeMaxPx = Math.max(200, imageCellMaxWidthPx, imageCellMaxHeightPx, THUMB_EMBED_PX + 120);

  const thumbEmbeds = await mapInBatches(
    tableRows,
    THUMB_FETCH_CONCURRENCY,
    (row, _idx) => fetchThumbEmbedded(row.thumbSrc, rasterDecodeMaxPx),
  );

  const thumbDisplayExtents = await Promise.all(
    thumbEmbeds.map(async (embed) => {
      if (!embed) return null;
      const nat = await measureNaturalSizeFromImageBuffer(embed.buffer);
      if (nat) {
        return thumbExtentsContain(nat.w, nat.h, imageCellMaxWidthPx, imageCellMaxHeightPx);
      }
      const side = Math.min(imageCellMaxWidthPx, imageCellMaxHeightPx);
      return { width: side, height: side };
    }),
  );

  const headerRow = sheet.addRow(headers);
  const headerRowNum = headerRow.number;
  const dataRowCount = tableRows.length;
  /** 데이터 N행 다음: 공백 1행(lastData+1) + 푸터 1행(lastData+2) */
  const lastDataRowNum = headerRowNum + dataRowCount;
  const footnoteRowNum = lastDataRowNum + 2;
  headerRow.eachCell({ includeEmpty: true }, (cell) => {
    cell.font = { bold: true };
  });

  tableRows.forEach((row, i) => {
    const embed = thumbEmbeds[i];
    const imageCellDisplay = embed ? '' : row.thumbSrc;
    sheet.addRow([
      row.displayNo ?? i + 1,
      imageCellDisplay,
      ...(includeArtist ? [row.artist] : []),
      row.title,
      row.year,
      row.material,
      row.sizeLine,
      ...(includePrice ? [row.priceLine] : []),
      ...(includeNote ? [row.noteLine] : []),
      ...(singleLineCaption ? [row.captionLine] : []),
    ]);
  });

  for (let i = 0; i < tableRows.length; i++) {
    const embed = thumbEmbeds[i];
    const rowNumOneBased = headerRowNum + 1 + i;
    const excelRow = sheet.getRow(rowNumOneBased);
    if (embed) {
      excelRow.height = rowHeightPts;
      const ext =
        thumbDisplayExtents[i] ??
        thumbExtentsContain(THUMB_EMBED_PX, THUMB_EMBED_PX, imageCellMaxWidthPx, imageCellMaxHeightPx);
      const imageId = workbook.addImage({
        // 브라우저에는 Buffer 없음 — ArrayBuffer → Uint8Array (ExcelJS 동일 지원)
        buffer: new Uint8Array(embed.buffer) as never,
        extension: embed.extension,
      });
      const padColEmu = Math.max(
        0,
        Math.round(((imageCellMaxWidthPx - ext.width) / 2) * EMU_PER_PIXEL_96),
      );
      const padRowEmu = Math.max(
        0,
        Math.round(((imageCellMaxHeightPx - ext.height) / 2) * EMU_PER_PIXEL_96),
      );
      sheet.addImage(imageId, {
        tl: {
          nativeCol: IMAGE_COL_ZERO_BASED,
          nativeRow: rowNumOneBased - 1,
          nativeColOff: padColEmu,
          nativeRowOff: padRowEmu,
        } as unknown as { col: number; row: number },
        ext,
        editAs: 'oneCell',
      });
    }
  }

  const lastDataCol =
    6 + (includeArtist ? 1 : 0) + (includePrice ? 1 : 0) + (includeNote ? 1 : 0)
      + (singleLineCaption ? 1 : 0);
  sheet.addRow([]);
  sheet.addRow([]);
  sheet.mergeCells(
    `A${footnoteRowNum}:${excelColumnLetter1Based(lastDataCol)}${footnoteRowNum}`,
  );
  const footerCell = sheet.getCell(footnoteRowNum, 1);
  footerCell.value = WORK_LIST_XLSX_EXPORT_FOOTNOTE_EN;
  footerCell.font = { italic: true, size: 10, color: { argb: 'FFB3B3B3' } };
  footerCell.alignment = { horizontal: 'right', vertical: 'middle' };

  getWorkListXlsxColumnWidthWeights(
    includeArtist,
    includePrice,
    includeNote,
    singleLineCaption,
  ).forEach((width, i) => {
    sheet.getColumn(i + 1).width = width;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}
